/**
 * Investor-Ready Dashboard
 * 
 * Comprehensive dashboard combining:
 * - Phase 1: Labor & Payroll Analytics
 * - Phase 2: Cash Flow & Financial Statements
 * - Phase 3: Owner Independence Score
 */
import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Users, DollarSign, TrendingUp, TrendingDown, Clock, Target,
  RefreshCw, Download, Calendar, BarChart3, PieChart, FileText,
  AlertTriangle, CheckCircle2, ArrowUpRight, ArrowDownRight,
  Building2, Wallet, CreditCard, Briefcase, Activity, Award,
  User, Settings, ChevronRight, Info
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Format currency with $ for admin area
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const formatCurrencyFull = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0);
};

// Metric Card Component
function MetricCard({ title, value, subtitle, icon: Icon, trend, trendValue, color = "blue", health }) {
  const colorClasses = {
    blue: "from-blue-500/10 to-blue-600/5 border-blue-500/20",
    green: "from-green-500/10 to-green-600/5 border-green-500/20",
    amber: "from-amber-500/10 to-amber-600/5 border-amber-500/20",
    red: "from-red-500/10 to-red-600/5 border-red-500/20",
    purple: "from-purple-500/10 to-purple-600/5 border-purple-500/20"
  };
  
  const iconColors = {
    blue: "bg-blue-500/20 text-blue-600",
    green: "bg-green-500/20 text-green-600",
    amber: "bg-amber-500/20 text-amber-600",
    red: "bg-red-500/20 text-red-600",
    purple: "bg-purple-500/20 text-purple-600"
  };

  return (
    <Card className={`bg-gradient-to-br ${colorClasses[color]}`}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-xl ${iconColors[color]}`}>
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{title}</p>
              <p className="text-2xl font-bold text-foreground">{value}</p>
              {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
            </div>
          </div>
          {trend && (
            <div className={`flex items-center gap-1 text-sm ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
              {trend === 'up' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              <span>{trendValue}</span>
            </div>
          )}
          {health && (
            <Badge variant={health === 'good' || health === 'excellent' ? 'default' : health === 'warning' ? 'secondary' : 'destructive'}>
              {health}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Independence Score Gauge
function IndependenceGauge({ score, rating, color }) {
  const gaugeColor = {
    green: "stroke-green-500",
    blue: "stroke-blue-500",
    amber: "stroke-amber-500",
    red: "stroke-red-500"
  }[color] || "stroke-blue-500";

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-32 h-32">
        <svg className="w-full h-full transform -rotate-90">
          <circle
            cx="64"
            cy="64"
            r="56"
            stroke="currentColor"
            strokeWidth="12"
            fill="none"
            className="text-muted/20"
          />
          <circle
            cx="64"
            cy="64"
            r="56"
            stroke="currentColor"
            strokeWidth="12"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={`${score * 3.52} 352`}
            className={gaugeColor}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold">{score}</span>
          <span className="text-xs text-muted-foreground">/ 100</span>
        </div>
      </div>
      <Badge className="mt-2" variant={color === 'green' ? 'default' : color === 'amber' ? 'secondary' : 'destructive'}>
        {rating}
      </Badge>
    </div>
  );
}

export default function InvestorReadyTab() {
  const [activeTab, setActiveTab] = useState('summary');
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  
  // Data states
  const [laborData, setLaborData] = useState(null);
  const [cashFlowData, setCashFlowData] = useState(null);
  const [plData, setPlData] = useState(null);
  const [balanceData, setBalanceData] = useState(null);
  const [independenceData, setIndependenceData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);

  const getDateRange = useCallback(() => {
    const today = new Date();
    let startDate;
    
    switch (period) {
      case 'week':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 7);
        break;
      case 'month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'quarter':
        startDate = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
        break;
      case 'year':
        startDate = new Date(today.getFullYear(), 0, 1);
        break;
      default:
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
    }
    
    return {
      start: startDate.toISOString().split('T')[0],
      end: today.toISOString().split('T')[0]
    };
  }, [period]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    const { start, end } = getDateRange();
    
    try {
      const [labor, cashFlow, pl, balance, independence, summary] = await Promise.all([
        api.get(`/admin/finance/investor/labor/dashboard?start_date=${start}&end_date=${end}`).then(r => r.data).catch(() => null),
        api.get(`/admin/finance/investor/cashflow/statement?start_date=${start}&end_date=${end}`).then(r => r.data).catch(() => null),
        api.get(`/admin/finance/investor/financial/profit-loss?start_date=${start}&end_date=${end}`).then(r => r.data).catch(() => null),
        api.get('/admin/finance/investor/financial/balance-sheet').then(r => r.data).catch(() => null),
        api.get(`/admin/finance/investor/independence/dashboard?start_date=${start}&end_date=${end}`).then(r => r.data).catch(() => null),
        api.get('/admin/finance/investor/summary').then(r => r.data).catch(() => null)
      ]);
      
      setLaborData(labor);
      setCashFlowData(cashFlow);
      setPlData(pl);
      setBalanceData(balance);
      setIndependenceData(independence);
      setSummaryData(summary);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load investor dashboard');
    }
    
    setLoading(false);
  }, [getDateRange]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="investor-ready-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading text-foreground">Investor-Ready Dashboard</h2>
          <p className="text-muted-foreground">Comprehensive business health metrics</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchAllData}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Quick Summary Cards */}
      {summaryData && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <MetricCard
            title="Revenue"
            value={formatCurrency(summaryData.key_metrics?.monthly_revenue)}
            icon={DollarSign}
            color="green"
          />
          <MetricCard
            title="Gross Margin"
            value={`${summaryData.key_metrics?.gross_margin}%`}
            icon={TrendingUp}
            color="blue"
          />
          <MetricCard
            title="Net Income"
            value={formatCurrency(summaryData.key_metrics?.net_income)}
            icon={Wallet}
            color={summaryData.key_metrics?.net_income >= 0 ? "green" : "red"}
          />
          <MetricCard
            title="Labor Cost"
            value={`${summaryData.key_metrics?.labor_percent}%`}
            icon={Users}
            health={summaryData.health_indicators?.labor_costs}
            color="purple"
          />
          <MetricCard
            title="Investment"
            value={formatCurrency(summaryData.key_metrics?.total_investment)}
            icon={Briefcase}
            color="amber"
          />
          <MetricCard
            title="Independence"
            value={summaryData.key_metrics?.independence_score}
            subtitle="/ 100"
            icon={Award}
            color={summaryData.key_metrics?.independence_score >= 70 ? "green" : summaryData.key_metrics?.independence_score >= 50 ? "amber" : "red"}
          />
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 w-full max-w-2xl">
          <TabsTrigger value="summary">Summary</TabsTrigger>
          <TabsTrigger value="labor">Labor</TabsTrigger>
          <TabsTrigger value="financial">Financial</TabsTrigger>
          <TabsTrigger value="independence">Independence</TabsTrigger>
        </TabsList>

        {/* Summary Tab */}
        <TabsContent value="summary" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Health Indicators */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  Business Health
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {summaryData?.health_indicators && Object.entries(summaryData.health_indicators).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between">
                    <span className="text-sm capitalize">{key.replace(/_/g, ' ')}</span>
                    <Badge variant={value === 'healthy' || value === 'good' || value === 'excellent' ? 'default' : 
                                   value === 'warning' || value === 'fair' ? 'secondary' : 'destructive'}>
                      {value}
                    </Badge>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Independence Score */}
            {independenceData && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Award className="w-5 h-5" />
                    Owner Independence
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col items-center py-4">
                  <IndependenceGauge
                    score={independenceData.independence_score?.score || 0}
                    rating={independenceData.independence_score?.rating || 'N/A'}
                    color={independenceData.independence_score?.rating_color || 'blue'}
                  />
                  <div className="mt-4 grid grid-cols-2 gap-4 w-full">
                    <div className="text-center">
                      <p className="text-2xl font-bold">{independenceData.owner_time?.weekly_average || 0}h</p>
                      <p className="text-xs text-muted-foreground">Weekly Hours</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold">{independenceData.delegation?.delegation_rate || 0}%</p>
                      <p className="text-xs text-muted-foreground">Delegation Rate</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Recommendations */}
          {independenceData?.recommendations?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Action Items</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {independenceData.recommendations.map((rec, idx) => (
                    <div key={idx} className={`p-3 rounded-lg border-l-4 ${
                      rec.priority === 'high' ? 'border-l-red-500 bg-red-50 dark:bg-red-900/20' :
                      rec.priority === 'medium' ? 'border-l-amber-500 bg-amber-50 dark:bg-amber-900/20' :
                      'border-l-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    }`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant={rec.priority === 'high' ? 'destructive' : rec.priority === 'medium' ? 'secondary' : 'default'} className="text-xs">
                          {rec.priority}
                        </Badge>
                        <span className="font-medium text-sm">{rec.area}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">{rec.issue}</p>
                      <p className="text-sm font-medium mt-1">{rec.action}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Labor Tab */}
        <TabsContent value="labor" className="space-y-4">
          {laborData && (
            <>
              {/* Labor Metrics */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <MetricCard
                  title="Total Staff"
                  value={laborData.summary?.total_staff || 0}
                  icon={Users}
                  color="blue"
                />
                <MetricCard
                  title="Total Hours"
                  value={`${laborData.summary?.total_hours || 0}h`}
                  subtitle={`${laborData.summary?.overtime_hours || 0}h overtime`}
                  icon={Clock}
                  color="purple"
                />
                <MetricCard
                  title="Labor Cost"
                  value={formatCurrency(laborData.summary?.total_labor_cost)}
                  icon={DollarSign}
                  color="amber"
                />
                <MetricCard
                  title="Labor %"
                  value={`${laborData.metrics?.labor_percent || 0}%`}
                  subtitle={`Target: ${laborData.metrics?.labor_benchmark?.min}-${laborData.metrics?.labor_benchmark?.max}%`}
                  icon={PieChart}
                  health={laborData.metrics?.labor_health}
                  color={laborData.metrics?.labor_health === 'good' ? 'green' : laborData.metrics?.labor_health === 'excellent' ? 'blue' : 'red'}
                />
              </div>

              {/* Staff Breakdown Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Staff Hours & Pay</CardTitle>
                </CardHeader>
                <CardContent>
                  {laborData.staff_breakdown?.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No clock records for this period</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Staff</TableHead>
                          <TableHead className="text-right">Regular</TableHead>
                          <TableHead className="text-right">Overtime</TableHead>
                          <TableHead className="text-right">Gross Pay</TableHead>
                          <TableHead className="text-right">FNPF</TableHead>
                          <TableHead className="text-right">Total Cost</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {laborData.staff_breakdown?.map((staff) => (
                          <TableRow key={staff.staff_id}>
                            <TableCell>
                              <div className="font-medium">{staff.staff_name}</div>
                              <div className="text-xs text-muted-foreground">{formatCurrencyFull(staff.hourly_rate)}/hr</div>
                            </TableCell>
                            <TableCell className="text-right">{staff.regular_hours}h</TableCell>
                            <TableCell className="text-right">
                              {staff.overtime_hours > 0 ? (
                                <span className="text-amber-600">{staff.overtime_hours}h</span>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="text-right">{formatCurrencyFull(staff.gross_pay)}</TableCell>
                            <TableCell className="text-right text-muted-foreground">{formatCurrencyFull(staff.fnpf_employer)}</TableCell>
                            <TableCell className="text-right font-semibold">{formatCurrencyFull(staff.total_cost)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* FNPF Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">FNPF Contributions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground">Employer Contribution ({laborData.fnpf?.employer_rate})</p>
                      <p className="text-2xl font-bold">{formatCurrency(laborData.fnpf?.total_employer_contribution)}</p>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground">Employee Deduction ({laborData.fnpf?.employee_rate})</p>
                      <p className="text-2xl font-bold">{formatCurrency(laborData.fnpf?.total_employee_contribution)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Financial Tab */}
        <TabsContent value="financial" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* P&L Summary */}
            {plData && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Profit & Loss</CardTitle>
                  <CardDescription>
                    {plData.current_period?.start} to {plData.current_period?.end}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b">
                    <span>Revenue</span>
                    <span className="font-bold text-green-600">{formatCurrency(plData.data?.revenue?.sales)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span>Cost of Goods Sold</span>
                    <span className="text-red-600">-{formatCurrency(plData.data?.cost_of_goods_sold)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b bg-muted/50 px-2 rounded">
                    <span className="font-medium">Gross Profit</span>
                    <div className="text-right">
                      <span className="font-bold">{formatCurrency(plData.data?.gross_profit)}</span>
                      <span className="text-sm text-muted-foreground ml-2">({plData.data?.gross_margin_percent}%)</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span>Payroll</span>
                    <span className="text-red-600">-{formatCurrency(plData.data?.operating_expenses?.payroll)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span>Overhead</span>
                    <span className="text-red-600">-{formatCurrency(plData.data?.operating_expenses?.overhead)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 bg-primary/10 px-2 rounded">
                    <span className="font-bold">Net Income</span>
                    <div className="text-right">
                      <span className={`font-bold text-lg ${plData.data?.net_income >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(plData.data?.net_income)}
                      </span>
                      <span className="text-sm text-muted-foreground ml-2">({plData.data?.operating_margin_percent}%)</span>
                    </div>
                  </div>
                  
                  {plData.comparison && (
                    <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm font-medium mb-2">vs Same Period Last Year</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex justify-between">
                          <span>Revenue</span>
                          <span className={plData.comparison.changes?.revenue_change >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {plData.comparison.changes?.revenue_change >= 0 ? '+' : ''}{plData.comparison.changes?.revenue_change}%
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Net Income</span>
                          <span className={plData.comparison.changes?.operating_income_change >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {plData.comparison.changes?.operating_income_change >= 0 ? '+' : ''}{plData.comparison.changes?.operating_income_change}%
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Balance Sheet Summary */}
            {balanceData && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Balance Sheet</CardTitle>
                  <CardDescription>As of {balanceData.as_of_date}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Assets */}
                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground mb-2">ASSETS</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Cash on Hand</span>
                        <span>{formatCurrency(balanceData.assets?.current_assets?.cash_on_hand)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Bank Balance</span>
                        <span>{formatCurrency(balanceData.assets?.current_assets?.bank_balance)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Accounts Receivable</span>
                        <span>{formatCurrency(balanceData.assets?.current_assets?.accounts_receivable)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Inventory</span>
                        <span>{formatCurrency(balanceData.assets?.current_assets?.inventory)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Fixed Assets (Net)</span>
                        <span>{formatCurrency(balanceData.assets?.fixed_assets?.net_fixed_assets)}</span>
                      </div>
                      <div className="flex justify-between font-bold border-t pt-1 mt-1">
                        <span>Total Assets</span>
                        <span>{formatCurrency(balanceData.assets?.total_assets)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Liabilities */}
                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground mb-2">LIABILITIES</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Accounts Payable</span>
                        <span>{formatCurrency(balanceData.liabilities?.current_liabilities?.accounts_payable)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Loans Payable</span>
                        <span>{formatCurrency(balanceData.liabilities?.long_term_liabilities?.loans_payable)}</span>
                      </div>
                      <div className="flex justify-between font-bold border-t pt-1 mt-1">
                        <span>Total Liabilities</span>
                        <span>{formatCurrency(balanceData.liabilities?.total_liabilities)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Equity */}
                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground mb-2">EQUITY</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Contributed Capital</span>
                        <span>{formatCurrency(balanceData.equity?.contributed_capital)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Retained Earnings</span>
                        <span className={balanceData.equity?.retained_earnings >= 0 ? '' : 'text-red-600'}>
                          {formatCurrency(balanceData.equity?.retained_earnings)}
                        </span>
                      </div>
                      <div className="flex justify-between font-bold border-t pt-1 mt-1">
                        <span>Total Equity</span>
                        <span>{formatCurrency(balanceData.equity?.total_equity)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Balance Check */}
                  <div className={`p-2 rounded text-center text-sm ${balanceData.balanced ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'}`}>
                    {balanceData.balanced ? (
                      <span className="flex items-center justify-center gap-2">
                        <CheckCircle2 className="w-4 h-4" />
                        Balance Sheet is Balanced
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Balance Sheet has discrepancy
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Cash Flow Statement */}
          {cashFlowData && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Cash Flow Statement</CardTitle>
                <CardDescription>
                  {cashFlowData.period?.start} to {cashFlowData.period?.end}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* Operating */}
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <h4 className="font-medium text-sm mb-3">Operating Activities</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Cash Sales</span>
                        <span className="text-green-600">+{formatCurrency(cashFlowData.operating_activities?.cash_from_sales)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Expenses Paid</span>
                        <span className="text-red-600">-{formatCurrency(cashFlowData.operating_activities?.cash_paid_for_expenses)}</span>
                      </div>
                      <div className="flex justify-between font-bold border-t pt-2 mt-2">
                        <span>Net</span>
                        <span className={cashFlowData.operating_activities?.net_cash_from_operations >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {formatCurrency(cashFlowData.operating_activities?.net_cash_from_operations)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Investing */}
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <h4 className="font-medium text-sm mb-3">Investing Activities</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Capital Expenditure</span>
                        <span className="text-red-600">-{formatCurrency(cashFlowData.investing_activities?.capital_expenditures)}</span>
                      </div>
                      <div className="flex justify-between font-bold border-t pt-2 mt-2">
                        <span>Net</span>
                        <span className={cashFlowData.investing_activities?.net_cash_from_investing >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {formatCurrency(cashFlowData.investing_activities?.net_cash_from_investing)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Financing */}
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <h4 className="font-medium text-sm mb-3">Financing Activities</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Loans</span>
                        <span className="text-green-600">+{formatCurrency(cashFlowData.financing_activities?.loans_received)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Equity</span>
                        <span className="text-green-600">+{formatCurrency(cashFlowData.financing_activities?.equity_contributions)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Owner Draws</span>
                        <span className="text-red-600">-{formatCurrency(cashFlowData.financing_activities?.owner_draws)}</span>
                      </div>
                      <div className="flex justify-between font-bold border-t pt-2 mt-2">
                        <span>Net</span>
                        <span className={cashFlowData.financing_activities?.net_cash_from_financing >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {formatCurrency(cashFlowData.financing_activities?.net_cash_from_financing)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Cash Position */}
                <div className="mt-4 p-4 bg-primary/10 rounded-lg">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div>
                      <p className="text-sm text-muted-foreground">Opening Balance</p>
                      <p className="text-lg font-medium">{formatCurrency(cashFlowData.cash_position?.opening_balance)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={cashFlowData.cash_position?.net_change >= 0 ? 'text-green-600' : 'text-red-600'}>
                        {cashFlowData.cash_position?.net_change >= 0 ? '+' : ''}{formatCurrency(cashFlowData.cash_position?.net_change)}
                      </span>
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Closing Balance</p>
                      <p className="text-2xl font-bold">{formatCurrency(cashFlowData.cash_position?.closing_balance)}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Independence Tab */}
        <TabsContent value="independence" className="space-y-4">
          {independenceData && (
            <>
              {/* Score Overview */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card className="lg:col-span-1">
                  <CardHeader>
                    <CardTitle className="text-lg">Independence Score</CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-col items-center py-4">
                    <IndependenceGauge
                      score={independenceData.independence_score?.score || 0}
                      rating={independenceData.independence_score?.rating || 'N/A'}
                      color={independenceData.independence_score?.rating_color || 'blue'}
                    />
                  </CardContent>
                </Card>

                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-lg">Score Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {independenceData.score_breakdown && Object.entries(independenceData.score_breakdown).map(([key, value]) => (
                      <div key={key} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                          <span className="font-medium">{value}/100</span>
                        </div>
                        <Progress value={value} className="h-2" />
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>

              {/* Owner Time & Delegation */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard
                  title="Weekly Hours"
                  value={`${independenceData.owner_time?.weekly_average || 0}h`}
                  subtitle={`Ideal: ${independenceData.owner_time?.ideal_weekly}h`}
                  icon={Clock}
                  color={independenceData.owner_time?.weekly_average <= 20 ? "green" : independenceData.owner_time?.weekly_average <= 40 ? "amber" : "red"}
                />
                <MetricCard
                  title="Strategic Focus"
                  value={`${independenceData.owner_time?.strategic_percent || 0}%`}
                  subtitle="of owner time"
                  icon={Target}
                  color={independenceData.owner_time?.strategic_percent >= 50 ? "green" : "amber"}
                />
                <MetricCard
                  title="Delegation Rate"
                  value={`${independenceData.delegation?.delegation_rate || 0}%`}
                  subtitle={`Ideal: ${independenceData.delegation?.ideal_rate}%`}
                  icon={Users}
                  color={independenceData.delegation?.delegation_rate >= 80 ? "green" : independenceData.delegation?.delegation_rate >= 60 ? "amber" : "red"}
                />
                <MetricCard
                  title="Auto-Processed"
                  value={`${independenceData.automation?.auto_processed_percent || 0}%`}
                  subtitle="of orders"
                  icon={Settings}
                  color="blue"
                />
              </div>

              {/* Staff Authority */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Team Structure</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {independenceData.staff_authority?.breakdown && Object.entries(independenceData.staff_authority.breakdown).map(([role, count]) => (
                      <div key={role} className="text-center p-4 bg-muted/50 rounded-lg">
                        <p className="text-3xl font-bold">{count}</p>
                        <p className="text-sm text-muted-foreground capitalize">{role}s</p>
                      </div>
                    ))}
                  </div>
                  {!independenceData.staff_authority?.has_managers && (
                    <div className="mt-4 p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-700 dark:text-amber-400">No Managers</p>
                        <p className="text-sm text-amber-600">Consider promoting a capable staff member to manager role to improve business independence.</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recommendations */}
              {independenceData.recommendations?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Recommendations</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {independenceData.recommendations.map((rec, idx) => (
                        <div key={idx} className={`p-4 rounded-lg ${
                          rec.priority === 'high' ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' :
                          rec.priority === 'medium' ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800' :
                          'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
                        }`}>
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-full ${
                              rec.priority === 'high' ? 'bg-red-100 dark:bg-red-900/40' :
                              rec.priority === 'medium' ? 'bg-amber-100 dark:bg-amber-900/40' :
                              'bg-blue-100 dark:bg-blue-900/40'
                            }`}>
                              {rec.priority === 'high' ? <AlertTriangle className="w-4 h-4 text-red-600" /> :
                               rec.priority === 'medium' ? <Info className="w-4 h-4 text-amber-600" /> :
                               <CheckCircle2 className="w-4 h-4 text-blue-600" />}
                            </div>
                            <div>
                              <p className="font-medium">{rec.area}</p>
                              <p className="text-sm text-muted-foreground mt-1">{rec.issue}</p>
                              <p className="text-sm font-medium mt-2">{rec.action}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
